import { Directionality } from 'BMapsSrc/integrations/properties_common';
import { PropApplicability } from 'BMapsSrc/model/ScopedPropertyGroup';
import { deepFreeze } from 'BMapsUtil/js_utils';
import { App } from 'BMapsSrc/BMapsApp';
import { JobDefinition, JobResultDestinations } from '../JobDefinition';

/**
 * Build the scoped-property metadata for an FEP method. Every FEP method
 * reports the same shape: a per-compound binding free energy (ΔG) plus its
 * uncertainty. Lower ΔG = stronger predicted binding (MINIMIZE).
 *
 * @param {string} scope - the property scope / jobTypeId (e.g. 'openfe_rbfe')
 * @param {string} methodLabel - human label for the method (e.g. 'OpenFE RBFE')
 * @param {string} dgDescription - how this method's ΔG is derived
 * @returns {object} scopedPropertyMetadata
 */
function makeFepMetadata(scope, methodLabel, dgDescription) {
    return deepFreeze({
        compound: {
            childGroups: {
                [scope]: {
                    metadata: {
                        label: `${methodLabel} properties`,
                        tooltipHeading: `${methodLabel} free energy`,
                    },
                    properties: {
                        dg: {
                            metadata: {
                                label: `ΔG (${methodLabel})`,
                                description: `${dgDescription} Lower is stronger predicted binding.`,
                                unit: 'kcal/mol',
                                directionality: Directionality.MINIMIZE,
                                defaultVisible: true,
                                propApplicability: PropApplicability.PERCOMPOUNDTARGET,
                            },
                        },
                        dg_err: {
                            metadata: {
                                label: `ΔG uncertainty (${methodLabel})`,
                                description: `Uncertainty of the ${methodLabel} ΔG estimate, in kcal/mol.`,
                                unit: 'kcal/mol',
                                directionality: Directionality.MINIMIZE,
                                propApplicability: PropApplicability.PERCOMPOUNDTARGET,
                            },
                        },
                    },
                },
            },
        },
    });
}

function normalizeName(name) {
    return String(name).replace(/[^A-Za-z0-9_.-]/g, '_');
}

/**
 * @typedef {object} FepResultsSummary
 * @property {Array<{name: string, dg: number, dg_err?: number, method?: string}>} scores
 *   per-ligand ΔG (empty when the network could not be solved)
 * @property {Array<{ligand_a: string, ligand_b: string, ddg: number, ddg_err?: number}>} edges
 *   per-edge ΔΔG (RBFE only)
 * @property {string} [warning] - why scores are missing / less reliable
 * @property {string} [status] - final cpaws / local-run status
 * @property {number} [finished] @property {number} [total] @property {number} [failed]
 * @property {string} [job_id] @property {string} [case_path]
 */

/**
 * Base class for FEP scoring jobs (OpenFE RBFE / ABFE, TMD RBFE). The compute
 * runs on the cpaws fleet or the in-house GPU box; the backend FEP workflow
 * returns a `{ scores: [{ name, dg, dg_err, method }], edges: [...] }` packet
 * (per-ligand ΔG keyed by ligand name = compound resSpec).
 *
 * `integrateResults` always stores a {@link FepResultsSummary} on the job's
 * local state (for the Free Energy results tab). It writes ΔG onto compounds
 * only when asked to apply (`options.apply` or `options.items`), matched by
 * NAME rather than position, since a disconnected MLE network can leave some
 * ligands unscored.
 *
 * @augments JobDefinition
 */
export class FepScoringJob extends JobDefinition {
    static resultDestination = JobResultDestinations.FREE_ENERGY;

    /** The property names this job writes onto a compound. */
    static scoreProps = ['dg', 'dg_err'];

    /**
     * @override
     * @returns {object}
     */
    getRequestMeta(jobrequest, stash) {
        const meta = {
            jobName: jobrequest?.jobName,
            method: this.constructor.jobTypeName,
            target: jobrequest?.params?.target || 'cpaws',
            ligandCount: jobrequest?.ligand_count,
        };
        // The stash itself only comes back with the results; these two ride
        // on the job list so the panel can scope a job to its compounds and
        // structure before results are loaded.
        if (stash?.ligandSpecs) meta.ligandSpecs = stash.ligandSpecs;
        if (stash?.structure?.name) meta.structure = stash.structure.name;
        return meta;
    }

    /**
     * The workspace compounds a job's ΔG may be applied to: the ones it was
     * submitted for (by resSpec), on the structure it was submitted from when
     * that is known. resSpecs are only unique within a structure, so with two
     * proteins loaded this is what keeps a ΔG off the wrong protein's
     * same-named compound. A job with no scoping information (older jobs)
     * falls back to every loaded compound, matched by name.
     *
     * @param {{ ligandSpecs?: string[], structure?: { name?: string } }} [scope]
     *   the job's stash (as returned with its results), or the equivalent
     *   fields from its request metadata
     * @param {import('BMapsModel').Compound[]} [loaded] - candidate pool
     *   (default: all loaded compounds)
     * @returns {import('BMapsModel').Compound[]}
     */
    /** The scoping fields {@link getRequestMeta} put on the job list. */
    static scopeFromRequestMeta(job) {
        const meta = job?.requestmeta;
        if (!meta?.ligandSpecs) return undefined;
        return { ligandSpecs: meta.ligandSpecs, structure: { name: meta.structure } };
    }

    static candidateCompounds(scope, loaded) {
        const pool = loaded || App.Workspace.getLoadedCompounds();
        const specs = new Set(scope?.ligandSpecs || []);
        const structureName = scope?.structure?.name;
        return pool.filter((c) => {
            if (specs.size > 0 && !specs.has(c.resSpec)) return false;
            if (structureName) {
                const { mapCase } = App.getDataParents(c) || {};
                if (mapCase?.displayName && mapCase.displayName !== structureName) return false;
            }
            return true;
        });
    }

    /**
     * Reduce a results packet to the summary the results tab renders.
     * @param {object} packet - the parsed job results packet
     * @returns {FepResultsSummary}
     */
    static summarizeResults(packet) {
        const jobResults = packet?.jobResults || {};
        return {
            scores: Array.isArray(jobResults.scores) ? jobResults.scores : [],
            edges: Array.isArray(jobResults.edges) ? jobResults.edges : [],
            warning: jobResults.warning,
            status: jobResults.status,
            finished: jobResults.finished,
            total: jobResults.total,
            failed: jobResults.failed,
            job_id: jobResults.job_id,
            case_path: jobResults.case_path,
        };
    }

    /**
     * Find the score entry for a compound, matching the cpaws/OpenFE ligand
     * name (= the SDF title, which BMaps sets to `resSpec`) against the
     * compound's identifiers, with a sanitized fallback.
     * @param {import('BMapsModel').Compound} cmpd
     * @param {Map<string, object>} byName
     * @returns {object|undefined}
     */
    findScoreForCompound(cmpd, byName) {
        const candidates = [cmpd?.resSpec, cmpd?.getName?.(), cmpd?.name]
            .filter((x) => x != null)
            .map(String);
        for (const c of candidates) {
            if (byName.has(c)) return byName.get(c);
        }
        const normMap = new Map([...byName.entries()].map(([k, v]) => [normalizeName(k), v]));
        for (const c of candidates) {
            const n = normalizeName(c);
            if (normMap.has(n)) return normMap.get(n);
        }
        return undefined;
    }

    /**
     * Store the results summary for the results tab and, when applying, write
     * the per-ligand ΔG onto the supplied workspace compounds as scoped
     * properties under this job's scope.
     * @override
     * @param {object} job - the redux job record (its jobid keys the local state)
     * @param {object|string} results - the retrieve-job-results response
     *   (`jobResults` packet plus the job's `stash`), or a bare packet
     * @param {import('../JobDefinition').IntegrateResultsOptions & {
     *   apply?: boolean, loaded?: import('BMapsModel').Compound[] }} [options]
     *   `items` are the compounds to apply to; `apply` without `items` applies
     *   to the compounds the job was submitted for, resolved from the results'
     *   stash against `loaded` (default: the workspace).
     * @returns {Promise<import('../JobDefinition').IntegrateResultsReturn & {
     *   summary?: FepResultsSummary, applied?: string[], missing?: string[] }>}
     */
    async integrateResults(job, results, options) {
        let packet = results;
        let error;
        try {
            if (!packet) return { error: 'Empty results' };
            if (typeof packet === 'string') packet = JSON.parse(packet);
            const summary = FepScoringJob.summarizeResults(packet);
            // The stash is only ever returned with the results (the polled
            // job list deliberately omits it), so this is the one place it
            // can be captured for scoping Apply.
            const stash = packet.stash || undefined;
            // Lazy: the redux access module imports the job registry, which
            // imports this class, so a top-level import would be a cycle.
            const { updateLocalJobState } = await import('BMapsRedux/jobs/access');
            if (job?.jobid) updateLocalJobState(job.jobid, { results: summary, stash });

            const { apply, loaded } = options || {};
            let { items } = options || {};
            const applying = !!apply || (Array.isArray(items) && items.length > 0);
            if (!applying) return { results: packet, summary };
            if (!Array.isArray(items) || items.length === 0) {
                items = FepScoringJob.candidateCompounds(
                    stash || FepScoringJob.scopeFromRequestMeta(job), loaded,
                );
            }

            if (summary.scores.length === 0) {
                return {
                    error: summary.warning || 'No per-compound ΔG to apply',
                    results: packet,
                    summary,
                };
            }
            const byName = new Map();
            for (const s of summary.scores) {
                if (s?.name != null) byName.set(String(s.name), s);
            }
            const applied = [];
            const appliedScores = new Set();
            for (const cmpd of items || []) {
                const score = this.findScoreForCompound(cmpd, byName);
                if (!score) continue;
                for (const prop of this.constructor.scoreProps) {
                    const value = score[prop];
                    if (value !== undefined && value !== null) {
                        cmpd.setProperty(this.jobTypeId, prop, value);
                    }
                }
                applied.push(cmpd.resSpec);
                appliedScores.add(score);
            }
            // Ligands the job scored but that are not in the workspace any more.
            const missing = summary.scores
                .filter((s) => !appliedScores.has(s))
                .map((s) => s.name);
            if (job?.jobid) updateLocalJobState(job.jobid, { applied, missing });
            return {
                results: packet, summary, applied, missing,
            };
        } catch (ex) {
            error = `Error loading ${this.jobTypeId} results`;
            console.error(error, ex);
        }
        return { error, results: packet };
    }
}

/**
 * OpenFE relative-FEP (RBFE) scoring job. cpaws program `openfe-rbfe`.
 * @augments FepScoringJob
 */
export class OpenFERbfeJob extends FepScoringJob {
    static jobTypeId = 'openfe_rbfe';
    static jobTypeName = 'OpenFE RBFE';
    static jobName = 'OpenFE RBFE Job';
    static scopedPropertyMetadata = makeFepMetadata(
        'openfe_rbfe', 'OpenFE RBFE',
        'Predicted binding free energy from OpenFE relative FEP (maximum-likelihood solve over the ΔΔG network), in kcal/mol.',
    );
}

/**
 * OpenFE absolute binding free energy (ABFE) job: one calculation per
 * ligand, no network, so a single compound is enough. cpaws program
 * `openfe-abfe`.
 * @augments FepScoringJob
 */
export class OpenFEAbfeJob extends FepScoringJob {
    static jobTypeId = 'openfe_abfe';
    static jobTypeName = 'OpenFE ABFE';
    static jobName = 'OpenFE ABFE Job';
    static scopedPropertyMetadata = makeFepMetadata(
        'openfe_abfe', 'OpenFE ABFE',
        'Predicted absolute binding free energy from OpenFE absolute FEP, in kcal/mol.',
    );
}

/**
 * TMD (timemachine) relative-FEP (RBFE) scoring job. cpaws program `tmd-rbfe`.
 * @augments FepScoringJob
 */
export class TmdRbfeJob extends FepScoringJob {
    static jobTypeId = 'tmd_rbfe';
    static jobTypeName = 'TMD RBFE';
    static jobName = 'TMD RBFE Job';
    static scopedPropertyMetadata = makeFepMetadata(
        'tmd_rbfe', 'TMD RBFE',
        'Predicted binding free energy from timemachine relative FEP (maximum-likelihood solve over the ΔΔG network), in kcal/mol.',
    );
}

/**
 * GROMACS relative-FEP (RBFE) job: pmx hybrid topologies, 16 λ windows with
 * Hamiltonian replica exchange, MBAR per edge, then a maximum-likelihood
 * solve over the network. Runs on ConiferGPU2 (the backend's MD worker,
 * `fep_gromacs_pipeline`); the packet is the same as the other FEP jobs'.
 * One receptor chain, protein only.
 * @augments FepScoringJob
 */
export class GromacsRbfeJob extends FepScoringJob {
    static jobTypeId = 'gromacs_rbfe';
    static jobTypeName = 'GROMACS RBFE';
    static jobName = 'GROMACS RBFE Job';
    static scopedPropertyMetadata = makeFepMetadata(
        'gromacs_rbfe', 'GROMACS RBFE',
        'Predicted binding free energy from GROMACS relative FEP (pmx hybrid topology, MBAR; maximum-likelihood solve over the ΔΔG network, mean set to zero), in kcal/mol.',
    );
}
